import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import '@chancenlogistik/client/components/logistik-component-edit-chancen-brief-variables';
import '@chancenlogistik/client/components/logistik-component-simple-product-item.js';
import {
  LogistikChancenBriefCartItem,
  LogistikLearningMaterialCartItem,
  type LogistikCartItem,
  type LogistikWorkshoppackCartiItem,
} from '@chancenwerk/db/LogistikCart.js';
import {
  ChancenBrief,
  LogistikProduct,
  LogistikProductSet,
  TravelCertificate,
  WorkshopPack,
} from '@chancenwerk/db/LogistikProduct.js';
import '@chancenwerk/design/components/cr-loading-state.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { WithId } from 'mongodb';
import { of, switchMap } from 'rxjs';
import { g_getProductCategoryString, isCartItemValid } from '../helper.js';

/**
 * @element logistik-component-cart-list
 */
@customElement('logistik-component-cart-list')
export class LogistikComponentCartList extends ChemistryLitElement {
  @property({ attribute: false }) cartItems = new RXController<Maybe<LogistikCartItem[]>>(this, []);

  @state() private readonly _products = new RXController(this, this.cartItems.observable, cartItems$ =>
    cartItems$.pipe(
      switchMap(cartItems =>
        cartItems
          ? LogistikProduct.getLogistikProductsByIDs(cartItems.map(i => i.productID))(LogistikProduct.allFields)
          : of([] as WithId<LogistikProduct>[]),
      ),
    ),
  );

  override render() {
    if (this._products.isLoading) return html` <cr-loading-state></cr-loading-state> `;
    return html`
      <cr-flex crossaxis-center space="md">
        ${this.cartItems.value && this.cartItems.value.length > 0
          ? this.cartItems.value.map(
              (item, i) =>
                html`
                  <logistik-page-cart-item
                    .cartItem=${item}
                    .cartItemIndex=${i}
                    .product=${this._products.value?.find(i => i._id === item.productID)}
                    .quantity=${item.quantity}
                  ></logistik-page-cart-item>
                `,
            )
          : html`
              <cr-empty-placeholder .img=${'shopping_cart'} .text=${'Warenkorb ist leer'}> </cr-empty-placeholder>
            `}
      </cr-flex>
      ${this.cartItems.value?.some(item => !this._products.value?.find(i => i._id === item.productID))
        ? html`
            <cr-button
              mode="text"
              icon="delete"
              tone="error"
              @click=${() => this.dispatchEvent(new CustomEvent('delete-non-available'))}
            >
              Nicht verfügbare Produkte aus dem Warenkorb nehmen
            </cr-button>
          `
        : nothing}
    `;
  }
}

const setProductSelectionSet = { _id: 1, title: 1, imageID: 1 } as const;
/**
 * @customElement logistik-page-cart-item
 */
@customElement('logistik-page-cart-item')
export class LogistikPageCartItem extends ChemistryLitElement {
  override styles() {
    return [...super.styles(), { ':host': { width: '100%' } }];
  }

  @property({ attribute: false }) cartItem: Maybe<
    LogistikCartItem | LogistikLearningMaterialCartItem | LogistikWorkshoppackCartiItem
  >;
  @property({ attribute: false }) cartItemIndex!: number;
  @property({ attribute: false }) product: RXController<any, Maybe<LogistikProduct>> = new RXController(this, null);
  @property({ type: Number, reflect: true }) quantity = 0;

  @state() private readonly setProducts = new RXController(this, this.product.observable, obs =>
    obs.pipe(
      switchMap(p =>
        p instanceof LogistikProductSet
          ? getByID(
              LogistikProduct,
              p.selectedProducts.map(a => a.productID),
            )(setProductSelectionSet)
          : of([] as NarrowedEntity<LogistikProduct, typeof setProductSelectionSet>[]),
      ),
    ),
  );

  @state() private startOpen = false;

  override connectedCallback(): void {
    super.connectedCallback();
    this.startOpen = !this.cartItem || !this.product.value || !isCartItemValid(this.cartItem, this.product.value);
  }

  override render() {
    if (this.product.isLoading) return html`<cr-loading-state></cr-loading-state>`;
    if (!this.product.value)
      return html`
        <cr-card w100>
          <x-flex space="sm" horizontal crossaxis-center>
            <x-flex space="sm" flex>
              <cr-h3> Produkt nicht gefunden </cr-h3>
              <cr-text> Dieses Produkt ist leider nicht mehr verfügbar. </cr-text>
            </x-flex>
          </x-flex>
        </cr-card>
      `;
    return html`
      <cr-card w100>
        <x-flex space="md">
          <cr-flex space="sm" horizontal crossaxis-center>
            ${this._renderImage()}
            <cr-flex space="sm" ${css({ flexGrow: '1' })}>
              <cr-flex horizontal>
                <cr-text bold> ${g_getProductCategoryString(this.product.value._class)} </cr-text>
                ${this._badge()}
              </cr-flex>
              <cr-h3>${this.product.value.title}</cr-h3>
              <logistik-component-product-availability
                status=${this.product.value.availability}
              ></logistik-component-product-availability>
              ${this.product.value instanceof WorkshopPack
                ? html`<cr-flex space="xs">
                    <cr-text
                      >Anzahl der Teilnehmenden :
                      ${(this.cartItem as LogistikWorkshoppackCartiItem)?.participantsQuantity}</cr-text
                    >
                    <cr-text
                      >Anzahl der Trainer:innen :
                      ${(this.cartItem as LogistikWorkshoppackCartiItem)?.trainersQuantity}</cr-text
                    >
                  </cr-flex>`
                : nothing}
            </cr-flex>
            <cr-flex space="md">
              <cr-flex horizontal crossaxis-center space="md">
                ${this._amountInputField()}
                <cr-button
                  mode="icon"
                  icon="delete"
                  tone="error"
                  @click=${() => {
                    this.dispatchEvent(
                      new CustomEvent('item-removed', {
                        detail: { productID: this.product.value!._id, index: this.cartItemIndex },
                        bubbles: true,
                        composed: true,
                      }),
                    );
                  }}
                ></cr-button>
              </cr-flex>
            </cr-flex>
          </cr-flex>
          ${this._chancenBriefeVariables()} ${this._renderSetProducts()}
        </x-flex>
      </cr-card>
    `;
  }

  private _renderImage(): Renderable {
    if (!this.product.value) return nothing;
    return html`
      <cr-flex>
        <cr-image
          disabled
          mode="contain"
          .value=${this.product.value.imageID}
          ${css({ height: '150px', width: '150px' })}
        ></cr-image>
      </cr-flex>
    `;
  }

  private get _isDigitalCartItem(): boolean {
    return (
      !!this.cartItem &&
      this.cartItem._class === LogistikLearningMaterialCartItem._class &&
      (this.cartItem as LogistikLearningMaterialCartItem).isDigital
    );
  }

  private get _isSelfPrintCartItem(): boolean {
    return (
      !!this.cartItem &&
      this.cartItem._class === LogistikChancenBriefCartItem._class &&
      (this.cartItem as LogistikChancenBriefCartItem).isSelfPrint
    );
  }

  private _badge(): Renderable {
    if (this._isDigitalCartItem)
      return html`
        <cr-chip ${css({ background: this.colors.primary, borderRadius: '5px', marginLeft: '6px', marginTop: '-3px' })}>
          <cr-text ${css({ fontSize: '12px', padding: '3px' })}> DIGITAL </cr-text>
        </cr-chip>
      `;
    else if (this._isSelfPrintCartItem)
      return html`
        <cr-chip ${css({ background: this.colors.primary, borderRadius: '5px', marginLeft: '6px', marginTop: '-3px' })}>
          <cr-text ${css({ fontSize: '12px', padding: '3px' })}> SELBSTDRUCK </cr-text>
        </cr-chip>
      `;
    else return nothing;
  }

  private _amountInputField(): Renderable {
    if (this._isDigitalCartItem || this._isSelfPrintCartItem) return nothing;
    else
      return html`
        <cr-flex horizontal crossaxis-center space="xs">
          <cr-text>Menge</cr-text>
          <cr-input
            type="number"
            placeholder-mode="hide"
            ${css({ width: '80px' })}
            .value=${this.quantity}
            @value-picked=${(e: ValueEvent<number>) =>
              this.dispatchEvent(
                new CustomEvent('amount-changed', {
                  detail: { amount: e.detail.value, productID: this.product.value!._id, index: this.cartItemIndex },
                  bubbles: true,
                  composed: true,
                }),
              )}
          ></cr-input>
        </cr-flex>
      `;
  }

  private _chancenBriefeVariables() {
    if (this.product.value instanceof ChancenBrief || this.product.value instanceof TravelCertificate) {
      return html`
        <cr-toggle-content label="CHANCENbrief Texte anzeigen / bearbeiten" .startOpen=${this.startOpen}>
          <logistik-component-edit-chancen-brief-variables
            .product=${this.product.value}
            .orderItem=${this.cartItem}
            showEmptyError
          ></logistik-component-edit-chancen-brief-variables>
        </cr-toggle-content>
      `;
    }
    return nothing;
  }

  private _renderSetProducts(): Renderable {
    if (!(this.product.value instanceof LogistikProductSet)) return nothing;
    return html`
      <cr-accordeon
        .items=${[
          {
            title: 'Enthaltene Produkte',
            text: html`
              <cr-flex space="md">
                ${this.setProducts.value?.map(
                  sp =>
                    html`
                      <logistik-component-simple-product-item
                        .product=${sp}
                        .quantity=${(this.product.value as LogistikProductSet).selectedProducts.find(
                          a => a.productID === sp._id,
                        )!.quantity}
                      >
                      </logistik-component-simple-product-item>
                    `,
                )}
              </cr-flex>
            `,
          },
        ]}
      >
      </cr-accordeon>
    `;
  }
}
